"use client";

import { Fragment, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import Link from "next/link";
import { useLocale, useTranslations } from "next-intl";
import { CountryFlag } from "@/features/profile/components/CountryFlag";
import {
  ChevronLeft,
  ChevronRight,
  Clock,
  Crosshair,
  MonitorPlay,
  Moon,
  Search,
  Snowflake,
  Star,
  Sun,
  TrendingUp,
} from "lucide-react";

import { cn } from "@/lib/utils";
import { cfImageUrl } from "@/lib/cloudflare-image";
import { formatNumber } from "@/lib/format-datetime";
import { Button, buttonVariants } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { SegmentedControl } from "@/components/ui/segmented-control";
import { Skeleton } from "@/components/ui/skeleton";
import { useContestStandings, useVirtualStandings } from "../hooks";
import { formatContestMinute, formatDateTime } from "../lib/format";
import { BAND_TEXT_ON_DARK, ratingBand, ratingTextClass } from "@/features/judge/lib/rating-band";
import { useContestDisplayZone } from "../lib/display-zone";
import {
  ICPC_PENALTY_MINUTES_PER_WRONG,
  balloonHex,
  hardestProblem,
  leaderOf,
  pendingTotal,
  ringFraction,
  rowLabel,
  solveTotal,
  zeroGroup,
} from "../lib/standings-summary";
import { initialStandingsTheme, rememberStandingsTheme, type StandingsTheme } from "../lib/standings-theme";
import { ResultSheet, resultAccessFor, type ResultAccess } from "./ResultSheet";
import { DivisionTag } from "./DivisionTag";
import { SponsorStrip, type SponsorLike } from "./SponsorStrip";
import type {
  ContestScoringFormat,
  ContestStandingsResponse,
  StandingsCell,
  StandingsRow,
  StandingsRowRating,
} from "../types";
import { smoothScrollBehavior } from "@/lib/scroll-behavior";

type StandingsProblem = ContestStandingsResponse["problems"][number];

/** `EX-SCB01`: the kiosk polls at this cadence; the server's cache bounds the cost. */
const KIOSK_REFETCH_MS = 10_000;
/** And turns the page at this one, when the field does not fit on one. */
const KIOSK_PAGE_TURN_MS = 20_000;
/** How long a tile that just changed keeps its ring (the redesign's "just updated"). */
const FRESH_MS = 6_000;

/**
 * `EX-SCB01`: who the row is, under the name.
 *
 * Drawn from `identity`, which the server fills with exactly the fields the
 * organizer flagged (`docs/29` §6.1). The people line prefers the flagged
 * roster (names or nicknames, never a coach) and falls back to the login
 * members the row always carried, so a contest whose organizer never opened the
 * setting reads exactly as it did. The logo is a flex sibling of the institution
 * name and is rendered only when the server sent an id: a team without one gets
 * the name alone, with no box kept empty for a logo (founder, 2026-09-11).
 */
export function IdentityLines({
  row,
  large = false,
  projector = false,
  dark = false,
}: {
  row: Pick<StandingsRow, "identity" | "members" | "name">;
  large?: boolean;
  /** `EX-DAY05`: the reveal on a projector, one step larger than the kiosk. */
  projector?: boolean;
  /** The Big screen's near-black ground. */
  dark?: boolean;
}) {
  const t = useTranslations("contest.standings");
  const locale = useLocale();
  const identity = row.identity;
  const people =
    identity?.members ??
    (row.members && row.members.length > 0 ? row.members.map((member) => member.name ?? "-") : null);
  const logoUrl = identity?.logoImageId ? cfImageUrl(identity.logoImageId, "avatar") : "";
  // `EX-ONS01`: the seat rides the institution line as a small mono chip, and
  // only when the server sent one; a row without a chair reserves nothing.
  const hasInstitutionLine = Boolean(
    logoUrl || identity?.institution || identity?.country || identity?.countryCode || identity?.seat,
  );
  const size = projector ? "text-base" : large ? "text-lg" : "text-[13px]";
  const muted = dark ? "text-neutral-300" : "text-neutral-600";

  return (
    <>
      {hasInstitutionLine ? (
        <p className={cn("mt-0.5 flex min-w-0 items-center gap-1.5", muted, size)}>
          {identity?.seat ? (
            <span
              className={cn(
                "shrink-0 rounded border px-1.5 font-mono text-[0.85em] font-semibold",
                dark ? "border-neutral-700 bg-neutral-900 text-neutral-100" : "border-[rgb(var(--border))] bg-neutral-50 text-neutral-800",
              )}
              title={identity.room ? `${identity.room} · ${identity.seat}` : identity.seat}
              aria-label={t("seatLabel", { label: identity.seat })}
            >
              {identity.seat}
            </span>
          ) : null}
          {logoUrl ? (
            // eslint-disable-next-line @next/next/no-img-element
            <img
              src={logoUrl}
              alt={t("logoAlt", { name: identity?.institution ?? row.name ?? "" })}
              className={cn("shrink-0 rounded-sm object-contain", projector ? "h-7 w-7" : large ? "h-6 w-6" : "h-5 w-5")}
              loading="lazy"
              // An id whose image is gone must not become a broken-image icon
              // beside the name: the row falls back to the name alone.
              onError={(event) => {
                event.currentTarget.style.display = "none";
              }}
            />
          ) : null}
          {/* `EX-IDN06`: the flag from the code, never an emoji; the country's name is its tooltip. */}
          {identity?.countryCode ? <CountryFlag code={identity.countryCode} locale={locale} /> : null}
          {identity?.institution ? <span className="truncate">{identity.institution}</span> : null}
          {identity?.country && !identity.countryCode ? (
            <span className={cn("shrink-0", dark ? "text-neutral-400" : "text-[rgb(var(--muted-foreground))]")}>
              {identity.institution ? `· ${identity.country}` : identity.country}
            </span>
          ) : null}
        </p>
      ) : null}
      {people ? (
        <p className={cn("mt-0.5", dark ? "text-neutral-400" : "text-[rgb(var(--muted-foreground))]", size, projector && "truncate")}>
          {people.join(", ")}
        </p>
      ) : null}
    </>
  );
}

/**
 * The states a cell can be in, in one place (`EX-PUB15`, redrawn 2026-09-30).
 * The rule the whole board follows: solid means scored, outlined means tried,
 * yellow means the verdict is on its way, stripes mean hidden on purpose, and
 * every one of them also carries a mark or words, so none is colour alone.
 *
 * The first solve is a **deeper** green with a gold edge and a star, ICPC's own
 * convention and the thing an onsite customer looks for first. `firstSolve` is
 * optional on the wire, so absent reads as "not first".
 *
 * `pending` is one field with two meanings: a submission still being judged, and
 * an attempt a freeze is hiding. The board says which by whether it is frozen
 * (`judging`), so a hidden try never looks like a slow judge and the other way
 * round.
 */
type CellState = "first" | "solved" | "partial" | "wrong" | "judging" | "pending" | "untouched";

export function cellState(cell: StandingsCell, format: ContestScoringFormat, judging = false): CellState {
  if (cell.solved) return cell.firstSolve ? "first" : "solved";
  if (format === "points" && cell.score > 0) return "partial";
  if (cell.pending > 0) return judging ? "judging" : "pending";
  if (cell.attempts > 0) return "wrong";
  return "untouched";
}

/* ── The board, design B (`EX-SCB03`, `EXAM_TASKS.md` Wave 24) ───────────────
 *
 * One card holds the board: the round's state and its three numbers in the
 * card's head, one column per problem whose letter sits in a ring that fills
 * with the share of tries that solved it, and every result a one-line pill.
 * Dark or light by the reader's choice; the Big screen is always dark and one
 * step larger (Locked Decisions 1 to 5). Every number is still the server's.
 */

type BoardSize = "board" | "projector";

/** The card's grounds, lines and text, per theme. Opaque, because sticky cells paint over what scrolls under them. */
const SURFACE: Readonly<
  Record<
    StandingsTheme,
    { card: string; cell: string; line: string; head: string; hover: string; text: string; muted: string; you: string; youHover: string; legend: string }
  >
> = {
  dark: {
    card: "bg-neutral-950 text-white shadow-[0_24px_48px_-28px_rgba(0,0,0,0.55)]",
    cell: "bg-neutral-950",
    line: "border-neutral-800",
    head: "bg-neutral-950 shadow-[inset_0_-1px_0_theme(colors.neutral.700)]",
    hover: "group-hover:bg-neutral-900",
    text: "text-white",
    muted: "text-neutral-400",
    you: "bg-scoreboard-you-night",
    youHover: "group-hover:bg-scoreboard-you-night",
    legend: "border-neutral-800 text-neutral-300",
  },
  light: {
    card: "bg-white text-neutral-900 shadow-card ring-1 ring-neutral-200",
    cell: "bg-white",
    line: "border-neutral-100",
    head: "bg-white shadow-[inset_0_-1px_0_theme(colors.neutral.200)]",
    hover: "group-hover:bg-neutral-50",
    text: "text-neutral-900",
    muted: "text-neutral-500",
    you: "bg-scoreboard-you-day",
    youHover: "group-hover:bg-scoreboard-you-day",
    legend: "border-neutral-100 bg-neutral-50 text-neutral-600",
  },
};

/**
 * A result's pill, per theme. Light's solve is solid dark green under white
 * text, never a pale tint (Locked Decision 2); the first solve is gold with a
 * star in both, the convention an onsite hall looks for first.
 */
const PILL_TONE: Readonly<Record<StandingsTheme, Readonly<Record<Exclude<CellState, "untouched">, string>>>> = {
  dark: {
    first: "bg-scoreboard-gold text-scoreboard-gold-ink",
    solved: "bg-scoreboard-solved-night text-scoreboard-solved-night-ink",
    partial: "bg-neutral-800 text-scoreboard-solved-night-ink",
    wrong: "bg-scoreboard-wrong-night text-scoreboard-wrong-night-ink",
    judging: "bg-scoreboard-judging-night text-scoreboard-judging-night-ink",
    pending:
      "text-scoreboard-frozen-night-ink shadow-[inset_0_0_0_1px_theme(colors.scoreboard.frozen-night-edge)] bg-[repeating-linear-gradient(135deg,theme(colors.scoreboard.frozen-night)_0_6px,theme(colors.scoreboard.frozen-night-2)_6px_12px)]",
  },
  light: {
    first: "bg-scoreboard-gold text-scoreboard-gold-ink",
    solved: "bg-scoreboard-solved-day text-white",
    partial: "bg-neutral-100 text-scoreboard-count-day",
    wrong: "bg-scoreboard-wrong-day text-scoreboard-wrong-day-ink shadow-[inset_0_0_0_1px_theme(colors.scoreboard.wrong-day-edge)]",
    judging:
      "bg-scoreboard-judging-day text-scoreboard-judging-day-ink shadow-[inset_0_0_0_1px_theme(colors.scoreboard.judging-day-edge)]",
    pending:
      "text-scoreboard-frozen-day-ink shadow-[inset_0_0_0_1px_theme(colors.scoreboard.frozen-day-edge)] bg-[repeating-linear-gradient(135deg,theme(colors.scoreboard.frozen-day)_0_6px,theme(colors.scoreboard.frozen-day-2)_6px_12px)]",
  },
};

/** The bar inside a points contest's partial pill, how much of the problem's points the row holds. */
const PARTIAL_BAR: Readonly<Record<StandingsTheme, string>> = {
  dark: "bg-scoreboard-solved-night",
  light: "bg-scoreboard-solved-day/20",
};

const MEDAL_DISC: Readonly<Record<number, string>> = {
  1: "bg-scoreboard-gold text-scoreboard-gold-ink",
  2: "bg-scoreboard-silver text-neutral-900",
  3: "bg-scoreboard-bronze text-scoreboard-bronze-ink",
};

/** The minus of "−2": a real minus sign, which sits on the same line as the digits. */
const MINUS = "−";

/**
 * One result as a one-line pill: the solve's time (or the points) with the
 * wrong tries before it as a small `+2`; `−2` for tries that did not solve;
 * `?2` with stripes for tries a freeze hides; a clock while the verdict is on
 * its way. An untouched cell is a dot, drawn by the caller.
 */
export function ResultPill({
  cell,
  format,
  judging,
  theme,
  size,
  phone = false,
  fresh,
}: {
  cell: StandingsCell;
  format: ContestScoringFormat;
  judging: boolean;
  theme: StandingsTheme;
  size: BoardSize;
  phone?: boolean;
  fresh: boolean;
}) {
  const t = useTranslations("contest.standings");
  const state = cellState(cell, format, judging);
  if (state === "untouched") return null;

  const wrong = cell.solved ? Math.max(0, cell.attempts - 1) : cell.attempts;
  let icon: ReactNode = null;
  let main: string;
  let tries: string | null = null;
  let title: string | undefined;

  if (state === "first" || state === "solved") {
    if (state === "first") icon = <Star className="fill-current" aria-hidden />;
    main =
      format === "icpc"
        ? cell.solvedAtMinute !== null
          ? formatContestMinute(cell.solvedAtMinute)
          : t("legendSolved")
        : String(cell.score);
    tries = wrong > 0 ? `+${wrong}` : null;
    title = state === "first" ? t("firstSolveTitle") : wrong > 0 ? t("triesBeforeTitle", { count: wrong }) : undefined;
  } else if (state === "partial") {
    main = String(cell.score);
    title = t("ofMax", { max: cell.maxScore });
  } else if (state === "wrong") {
    // `docs/29` §6.3: an unsolved cell is worth zero however many tries it took.
    main = format === "points" ? String(cell.score) : `${MINUS}${cell.attempts}`;
    title = t("triesNotSolvedTitle", { count: cell.attempts });
  } else if (state === "judging") {
    icon = <Clock strokeWidth={2.4} aria-hidden />;
    main = t("judging");
    tries = cell.attempts > 0 ? `${MINUS}${cell.attempts}` : null;
    title = t("judgingTitle");
  } else {
    // A frozen board's hidden tries are drawn even when they are the only thing
    // in the cell: knowing the row above has tries you cannot see is the
    // frozen board's whole tension.
    main = t("pendingCount", { count: cell.pending });
    tries = cell.attempts > 0 ? `${MINUS}${cell.attempts}` : null;
    title = t("hiddenTitle");
  }

  // A solve or part of the points with tries after the freeze behind it.
  const hidden = cell.pending > 0 && (state === "first" || state === "solved" || state === "partial");
  const ratio = cell.maxScore > 0 ? Math.min(100, Math.max(0, Math.round((cell.score / cell.maxScore) * 100))) : 0;
  const projector = size === "projector";

  return (
    <span
      title={title}
      data-state={state}
      data-fresh={fresh ? "true" : undefined}
      className={cn(
        "relative mx-auto inline-flex w-full items-center justify-center gap-1 overflow-hidden whitespace-nowrap rounded-full font-bold tabular-nums transition-[transform,filter] duration-150 ease-out motion-reduce:transition-none",
        projector
          ? "h-11 min-w-[88px] max-w-[136px] px-3 text-lg [&_svg]:h-4 [&_svg]:w-4"
          : phone
            ? "h-[26px] px-1.5 text-xs [&_svg]:h-2.5 [&_svg]:w-2.5"
            : "h-7 min-w-[58px] max-w-[96px] px-[9px] text-[12.5px] [&_svg]:h-[11px] [&_svg]:w-[11px]",
        PILL_TONE[theme][state],
        fresh && "outline outline-2 outline-offset-2 outline-primary animate-in fade-in-0 duration-200",
      )}
    >
      {state === "partial" ? (
        <i aria-hidden className={cn("absolute inset-y-0 left-0", PARTIAL_BAR[theme])} style={{ width: `${ratio}%` }} />
      ) : null}
      <span className="relative inline-flex items-center gap-1">
        {icon}
        {main}
      </span>
      {tries ? (
        <span className={cn("relative self-start font-bold", projector ? "mt-2 text-xs" : "mt-[5px] text-[9.5px]")}>{tries}</span>
      ) : null}
      {hidden ? (
        <span className={cn("relative self-start font-bold", projector ? "mt-2 text-xs" : "mt-[5px] text-[9.5px]")}>
          {t("pendingCount", { count: cell.pending })}
        </span>
      ) : null}
    </span>
  );
}

/** The dot an untouched cell wears, so the grid still reads as a grid. */
function Dot({ theme }: { theme: StandingsTheme }) {
  return (
    <span aria-hidden className={cn("mx-auto block h-1 w-1 rounded-full", theme === "dark" ? "bg-neutral-700" : "bg-neutral-300")} />
  );
}

/**
 * A balloon in the colour the organizer set for the problem, on its string
 * above the letter (Locked Decision 4). A thin outline lets a white, yellow or
 * black balloon read on either ground. `label` false hides it from a screen
 * reader where the same column already announced it (the phone's strip).
 */
export function Balloon({
  color,
  theme,
  size,
  phone = false,
  label = true,
}: {
  color: NonNullable<StandingsProblem["balloonColor"]>;
  theme: StandingsTheme;
  size: BoardSize;
  phone?: boolean;
  label?: boolean;
}) {
  const t = useTranslations("contest.standings");
  const hex = balloonHex(color);
  if (!hex) return null;
  return (
    <svg
      role={label ? "img" : undefined}
      aria-label={label ? t("balloonLabel", { name: color.name }) : undefined}
      aria-hidden={label ? undefined : true}
      viewBox="0 0 24 36"
      className={cn("block", size === "projector" ? "-mb-1 h-[30px] w-5" : phone ? "-mb-[3px] h-[17px] w-[11px]" : "-mb-[3px] h-[21px] w-[14px]")}
    >
      <path
        d="M12 1C6.5 1 2.5 5.3 2.5 10.8c0 5.6 4.3 9.6 9.5 12 5.2-2.4 9.5-6.4 9.5-12C21.5 5.3 17.5 1 12 1z"
        fill={hex}
        strokeWidth="0.9"
        className={theme === "dark" ? "stroke-white/30" : "stroke-black/20"}
      />
      <path d="M12 22.4l-2.6 3.3h5.2z" fill={hex} />
      <path
        d="M12 25.7c-1.7 3 1.7 4.6 0 9.3"
        fill="none"
        strokeWidth="1.3"
        strokeLinecap="round"
        className={theme === "dark" ? "stroke-neutral-500" : "stroke-neutral-400"}
      />
      <ellipse cx="8" cy="8" rx="2" ry="3.4" fill="white" opacity="0.45" transform="rotate(25 8 8)" />
    </svg>
  );
}

/** The ring's circumference at r = 15.5 in a 36 box. */
const RING_C = 2 * Math.PI * 15.5;

/**
 * The letter in a ring that fills with the share of tries that solved it
 * (Locked Decision 1), a link to the problem named by its title (decision 5).
 * On the Big screen nobody clicks, so it is not a link there.
 */
export function ProblemRing({
  problem,
  theme,
  size,
  phone = false,
  href,
  label,
}: {
  problem: StandingsProblem;
  theme: StandingsTheme;
  size: BoardSize;
  phone?: boolean;
  href: string | null;
  label: string;
}) {
  const fraction = ringFraction(problem);
  const box = size === "projector" ? "h-11 w-11" : phone ? "h-[26px] w-[26px]" : "h-[30px] w-[30px]";
  const ring = (
    <>
      <svg viewBox="0 0 36 36" aria-hidden className={cn("block", box)}>
        <circle
          cx="18"
          cy="18"
          r="15.5"
          strokeWidth="3"
          className={theme === "dark" ? "fill-neutral-900 stroke-neutral-800" : "fill-neutral-50 stroke-neutral-200"}
        />
        <circle
          cx="18"
          cy="18"
          r="15.5"
          fill="none"
          strokeWidth="3"
          strokeLinecap="round"
          strokeDasharray={`${(fraction * RING_C).toFixed(2)} ${RING_C.toFixed(2)}`}
          transform="rotate(-90 18 18)"
          className={cn(
            theme === "dark" ? "stroke-scoreboard-ring-night" : "stroke-scoreboard-ring-day",
            "transition-[stroke-dasharray] duration-500 ease-out motion-reduce:transition-none",
            fraction === 0 && "hidden",
          )}
        />
      </svg>
      <span
        className={cn(
          "absolute inset-0 grid place-items-center font-bold",
          size === "projector" ? "text-lg" : phone ? "text-[11.5px]" : "text-[13px]",
          theme === "dark" ? "text-white" : "text-neutral-900",
          href && (theme === "dark" ? "group-hover/ring:text-accent-peach" : "group-hover/ring:text-primary-ink"),
        )}
      >
        {problem.letter}
      </span>
    </>
  );
  const shared = cn("group/ring relative block rounded-full", box);
  return href ? (
    <Link
      href={href}
      aria-label={label}
      title={label}
      data-ring={fraction.toFixed(3)}
      className={cn(
        shared,
        "transition-transform duration-150 ease-out hover:scale-105 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary motion-reduce:transition-none motion-reduce:hover:scale-100",
      )}
    >
      {ring}
    </Link>
  ) : (
    <span className={shared} data-ring={fraction.toFixed(3)}>
      {ring}
    </span>
  );
}

/** solved over tried beside the ring, the words in its title. */
export function ColumnCounts({
  problem,
  theme,
  size,
  withTitle,
}: {
  problem: StandingsProblem;
  theme: StandingsTheme;
  size: BoardSize;
  withTitle: boolean;
}) {
  const t = useTranslations("contest.standings");
  if (problem.solvedCount === undefined) return null;
  const tried = problem.attemptedCount ?? problem.solvedCount;
  return (
    <span
      title={withTitle ? t("columnStatsTitle", { solved: problem.solvedCount, attempted: tried }) : undefined}
      className={cn(
        "flex flex-col text-left font-semibold leading-[1.15] tabular-nums",
        size === "projector" ? "text-sm" : "text-[10.5px]",
        theme === "dark" ? "text-neutral-400" : "text-neutral-500",
      )}
    >
      <b className={cn(size === "projector" ? "text-lg" : "text-[13px]", theme === "dark" ? "text-scoreboard-ring-night" : "text-scoreboard-count-day")}>
        {problem.solvedCount}
      </b>
      <span>{`/${tried}`}</span>
    </span>
  );
}

/**
 * The rank: a medal disc for the top three on a final board only, never while it is frozen.
 * `EX-DIV03`: an unofficial row has no rank (`null`) and wears a dashed U, named for a screen reader.
 */
export function RankMark({ rank, final, theme, size }: { rank: number | null; final: boolean; theme: StandingsTheme; size: BoardSize }) {
  const t = useTranslations("contest.division");
  if (rank === null) {
    return (
      <span
        data-unofficial-mark
        title={t("unofficial")}
        className={cn(
          "inline-grid place-items-center rounded-md border border-dashed font-bold",
          size === "projector" ? "h-10 w-10 text-lg" : "h-6 w-6 text-xs",
          theme === "dark" ? "border-neutral-500 text-neutral-300" : "border-neutral-400 text-neutral-600",
        )}
      >
        <span aria-hidden>U</span>
        <span className="sr-only">{t("unofficial")}</span>
      </span>
    );
  }
  const medal = final ? MEDAL_DISC[rank] : undefined;
  if (medal) {
    return (
      <span
        data-medal={rank}
        className={cn(
          "inline-grid place-items-center rounded-full font-bold tabular-nums",
          size === "projector" ? "h-10 w-10 text-lg" : "h-6 w-6 text-xs",
          medal,
        )}
      >
        {rank}
      </span>
    );
  }
  return (
    <span
      className={cn(
        "font-semibold tabular-nums",
        size === "projector" ? "text-xl" : "text-[13.5px]",
        theme === "dark" ? "text-neutral-400" : "text-neutral-500",
      )}
    >
      {rank}
    </span>
  );
}

/**
 * The rating cell - `EX-PUB07`, `docs/29` §7. Drawn from the row's ledger
 * entry and never computed here: a first rated contest shows "unrated" as the
 * before, and a zero delta is drawn as a zero rather than left blank.
 */
function RatingCells({
  rating,
  theme,
  cellClass,
}: {
  rating: StandingsRowRating | null;
  theme: StandingsTheme;
  cellClass: string;
}) {
  const t = useTranslations("contest.standings");
  const muted = SURFACE[theme].muted;
  if (!rating) {
    return (
      <td className={cn(cellClass, "text-center text-xs", muted)} colSpan={2}>
        -
      </td>
    );
  }
  const tone =
    rating.delta > 0
      ? theme === "dark"
        ? "bg-scoreboard-solved-night text-scoreboard-solved-night-ink"
        : "bg-neutral-100 text-scoreboard-count-day"
      : rating.delta < 0
        ? theme === "dark"
          ? "bg-scoreboard-wrong-night text-scoreboard-wrong-night-ink"
          : "bg-scoreboard-wrong-day text-scoreboard-wrong-day-ink"
        : theme === "dark"
          ? "bg-neutral-800 text-neutral-300"
          : "bg-neutral-100 text-neutral-500";
  return (
    <>
      <td className={cn(cellClass, "text-center tabular-nums")}>
        <span className="flex flex-col items-center leading-[1.15]">
          <b className="text-sm">{rating.after}</b>
          <span className={cn("text-[10.5px]", muted)}>{rating.wasUnrated ? t("unrated") : rating.before}</span>
        </span>
      </td>
      <td className={cn(cellClass, "text-center")}>
        <span className={cn("inline-block rounded-full px-2 py-0.5 text-xs font-bold tabular-nums", tone)}>
          {rating.delta > 0 ? `+${rating.delta}` : rating.delta}
        </span>
      </td>
    </>
  );
}

/** `EX-DIV03`: "Unofficial" beside the name of a row outside the division. */
function UnofficialPill({ theme }: { theme: StandingsTheme }) {
  const t = useTranslations("contest.division");
  return (
    <span
      className={cn(
        "shrink-0 rounded-full border px-2 py-px text-[10.5px] font-semibold",
        theme === "dark" ? "border-neutral-700 text-neutral-300" : "border-neutral-300 text-neutral-600",
      )}
    >
      {t("unofficial")}
    </span>
  );
}

/** `EX-VRT01`: "You · virtual" on the reader's own row of their virtual board. */
function VirtualPill() {
  const t = useTranslations("contest.virtual");
  return (
    <span className="shrink-0 rounded-full bg-primary px-2 py-0.5 text-[10.5px] font-bold tracking-wide text-white motion-safe:animate-pulse">
      {t("youVirtual")}
    </span>
  );
}

function isZeroRow(row: StandingsRow, format: ContestScoringFormat): boolean {
  return format === "icpc" ? row.solved === 0 : row.score === 0;
}

/** A name in its rating band's colour, the band's own shade for the ground it sits on. */
function nameTone(rating: number | null | undefined, theme: StandingsTheme): string | undefined {
  if (typeof rating !== "number") return undefined;
  return theme === "dark" ? BAND_TEXT_ON_DARK[ratingBand(rating)] : ratingTextClass(rating);
}

/** The sticky columns, rank then name; widths per size so the name sticks just right of the rank. */
const STICKY: Readonly<Record<BoardSize, { rank: string; name: string }>> = {
  board: { rank: "sticky left-0 z-10 w-[52px] min-w-[52px]", name: "sticky left-[52px] z-10 min-w-[200px]" },
  projector: { rank: "sticky left-0 z-10 w-[76px] min-w-[76px]", name: "sticky left-[76px] z-10 min-w-[280px]" },
};

/**
 * `EX-SCB05`: a result that opens its code is a button around its pill, named
 * for whose code and which letter; one that does not stays a picture.
 */
function OpenableResult({
  label,
  onOpen,
  children,
}: {
  label: string;
  onOpen: (() => void) | null;
  children: ReactNode;
}) {
  if (!onOpen) return <>{children}</>;
  return (
    <Button
      type="button"
      variant={null}
      size={null}
      aria-haspopup="dialog"
      aria-label={label}
      onClick={onOpen}
      data-opens-result
      className="w-full rounded-full p-0 transition-transform duration-150 ease-out hover:scale-[1.04] focus-visible:ring-offset-2 motion-reduce:transition-none motion-reduce:hover:scale-100"
    >
      {children}
    </Button>
  );
}

function BoardRow({
  row,
  format,
  isYou,
  showRating,
  final,
  judging,
  theme,
  size,
  fresh,
  onOpen,
}: {
  row: StandingsRow;
  format: ContestScoringFormat;
  isYou: boolean;
  showRating: boolean;
  final: boolean;
  judging: boolean;
  theme: StandingsTheme;
  size: BoardSize;
  fresh: ReadonlySet<string>;
  /** `EX-SCB05`: set when this row's results open for the reader. */
  onOpen: ((cell: StandingsCell) => void) | null;
}) {
  const t = useTranslations("contest.standings");
  const tb = useTranslations("judge.ratingBands");
  const surface = SURFACE[theme];
  const zero = isZeroRow(row, format);
  const projector = size === "projector";
  const ground = isYou ? surface.you : surface.cell;
  const hover = isYou ? surface.youHover : surface.hover;
  const cellClass = cn("border-b transition-colors duration-150", surface.line, ground, hover, projector ? "px-3 py-2.5" : "px-2 py-[7px]");
  const rating = row.currentRating;

  return (
    <tr id={`standings-row-${row.entryId}`} className="group">
      <td
        className={cn(
          cellClass,
          STICKY[size].rank,
          "text-center",
          isYou && "shadow-[inset_3px_0_0_theme(colors.primary.DEFAULT)]",
        )}
      >
        <RankMark rank={row.rank} final={final} theme={theme} size={size} />
      </td>
      <td className={cn(cellClass, STICKY[size].name, "pl-1.5 pr-3 text-left")}>
        <div className="flex min-w-0 items-center gap-2">
          {/* `EX-PUB23`: the name wears its band's colour; an unrated one stays plain. */}
          <span
            className={cn(
              "truncate",
              projector ? "text-2xl" : "text-[14.5px]",
              zero ? cn("font-medium", surface.muted) : cn("font-semibold", surface.text),
              nameTone(rating, theme),
            )}
            title={typeof rating === "number" ? tb(ratingBand(rating)) : undefined}
          >
            {rowLabel(row)}
          </span>
          {isYou && !row.virtual ? (
            <span className="shrink-0 rounded-full bg-primary-ink px-2 py-0.5 text-[10.5px] font-bold tracking-wide text-white">
              {t("you")}
            </span>
          ) : null}
          {row.virtual ? <VirtualPill /> : null}
          {row.unofficial ? <UnofficialPill theme={theme} /> : null}
        </div>
        <IdentityLines row={row} large={projector} dark={theme === "dark"} />
      </td>
      <td className={cn(cellClass, "text-center tabular-nums")}>
        <span className="inline-flex items-baseline gap-2">
          <b className={cn(projector ? "text-3xl" : "text-[17px]", zero ? surface.muted : surface.text)}>
            {format === "icpc" ? row.solved : row.score}
          </b>
          {format === "icpc" ? (
            <span className={cn("font-medium", projector ? "text-lg" : "text-[12.5px]", surface.muted)}>
              {zero ? "–" : row.penaltyMinutes}
            </span>
          ) : null}
        </span>
      </td>
      {row.cells.map((cell) => (
        <td key={cell.letter} className={cn(cellClass, "text-center")}>
          {cellState(cell, format, judging) === "untouched" ? (
            <Dot theme={theme} />
          ) : (
            <OpenableResult
              label={t("openResult", { name: rowLabel(row), letter: cell.letter })}
              onOpen={onOpen ? () => onOpen(cell) : null}
            >
              <ResultPill
                // A pill that changes remounts, so its fade-in plays once for the change.
                key={`${cell.solved}:${cell.attempts}:${cell.pending}:${cell.score}`}
                cell={cell}
                format={format}
                judging={judging}
                theme={theme}
                size={size}
                fresh={fresh.has(`${row.entryId}:${cell.letter}`)}
              />
            </OpenableResult>
          )}
        </td>
      ))}
      {showRating ? <RatingCells rating={row.rating} theme={theme} cellClass={cellClass} /> : null}
    </tr>
  );
}

/**
 * The same row on a phone: rank, name and score on one line, then one pill
 * per problem, each under its letter in the sticky strip above (two rows of
 * pills above six problems).
 */
function PhoneRow({
  row,
  format,
  isYou,
  final,
  judging,
  theme,
  columns,
  fresh,
  onOpen,
}: {
  row: StandingsRow;
  format: ContestScoringFormat;
  isYou: boolean;
  final: boolean;
  judging: boolean;
  theme: StandingsTheme;
  columns: string;
  fresh: ReadonlySet<string>;
  onOpen: ((cell: StandingsCell) => void) | null;
}) {
  const t = useTranslations("contest.standings");
  const tb = useTranslations("judge.ratingBands");
  const surface = SURFACE[theme];
  const zero = isZeroRow(row, format);

  return (
    <div
      id={`standings-card-${row.entryId}`}
      className={cn("border-b px-4 py-[11px]", surface.line, isYou && cn(surface.you, "shadow-[inset_3px_0_0_theme(colors.primary.DEFAULT)]"))}
    >
      <div className="flex items-center gap-[9px]">
        <span className="w-6 shrink-0 text-center">
          <RankMark rank={row.rank} final={final} theme={theme} size="board" />
        </span>
        <span
          className={cn(
            "min-w-0 flex-1 truncate text-[14.5px]",
            zero ? cn("font-medium", surface.muted) : cn("font-semibold", surface.text),
            nameTone(row.currentRating, theme),
          )}
          title={typeof row.currentRating === "number" ? tb(ratingBand(row.currentRating)) : undefined}
        >
          {rowLabel(row)}
        </span>
        {isYou && !row.virtual ? (
          <span className="shrink-0 rounded-full bg-primary-ink px-[7px] py-0.5 text-[10px] font-bold text-white">{t("you")}</span>
        ) : null}
        {row.virtual ? <VirtualPill /> : null}
        {row.unofficial ? <UnofficialPill theme={theme} /> : null}
        <span className="flex shrink-0 items-baseline gap-1.5 tabular-nums">
          {format === "icpc" ? (
            <>
              <b className={cn("text-base", zero ? surface.muted : surface.text)}>{row.solved}</b>
              {zero ? null : <span className={cn("text-xs", surface.muted)}>{t("penaltyMin", { minutes: row.penaltyMinutes })}</span>}
            </>
          ) : (
            <b className={cn("text-base", zero ? surface.muted : surface.text)}>{t("scoreShort", { score: row.score })}</b>
          )}
        </span>
      </div>
      <div className="pl-[33px]">
        <IdentityLines row={row} dark={theme === "dark"} />
      </div>
      <div className="mt-[9px] grid gap-1.5" style={{ gridTemplateColumns: columns }}>
        {row.cells.map((cell) =>
          cellState(cell, format, judging) === "untouched" ? (
            <span key={cell.letter} className="grid h-[26px] place-items-center">
              <Dot theme={theme} />
            </span>
          ) : (
            <OpenableResult
              key={`${cell.letter}:${cell.solved}:${cell.attempts}:${cell.pending}:${cell.score}`}
              label={t("openResult", { name: rowLabel(row), letter: cell.letter })}
              onOpen={onOpen ? () => onOpen(cell) : null}
            >
              <ResultPill
                cell={cell}
                format={format}
                judging={judging}
                theme={theme}
                size="board"
                phone
                fresh={fresh.has(`${row.entryId}:${cell.letter}`)}
              />
            </OpenableResult>
          ),
        )}
      </div>
    </div>
  );
}

/**
 * `EX-PUB22`: what the pills mean, and which rows this page holds. Judging
 * appears only where something is being judged, hidden tries only on a frozen
 * board, part of a problem's points only where a contest scores by points.
 */
function Legend({
  format,
  frozen,
  hasJudging,
  theme,
  size,
  className,
  count,
  hint,
}: {
  format: ContestScoringFormat;
  frozen: boolean;
  hasJudging: boolean;
  theme: StandingsTheme;
  size: BoardSize;
  className?: string;
  count?: string | null;
  /** `EX-SCB05`: which results open, when any do. */
  hint?: string | null;
}) {
  const t = useTranslations("contest.standings");
  const tones = PILL_TONE[theme];
  const items: Array<{ key: string; tone: string; label: string; content: ReactNode }> = [
    {
      key: "first",
      tone: tones.first,
      label: t("firstSolveTitle"),
      content: (
        <>
          <Star className="fill-current" aria-hidden />
          {format === "icpc" ? "0:21" : "100"}
        </>
      ),
    },
    { key: "solved", tone: tones.solved, label: t("legendSolved"), content: <>{format === "icpc" ? "0:44" : "100"}</> },
    ...(format !== "points"
      ? [
          {
            key: "tries",
            tone: tones.solved,
            label: t("legendTries"),
            content: (
              <>
                {format === "icpc" ? "0:44" : "80"}
                <span className="self-start text-[9px]">+2</span>
              </>
            ),
          },
        ]
      : []),
    ...(format === "points" ? [{ key: "partial", tone: tones.partial, label: t("legendPartial"), content: <>60</> }] : []),
    { key: "wrong", tone: tones.wrong, label: t("legendWrong"), content: <>{format === "points" ? "0" : `${MINUS}2`}</> },
    ...(!frozen && hasJudging
      ? [{ key: "judging", tone: tones.judging, label: t("legendJudging"), content: <Clock strokeWidth={2.4} aria-hidden /> }]
      : []),
    ...(frozen ? [{ key: "pending", tone: tones.pending, label: t("legendPending"), content: <>?2</> }] : []),
  ];
  const projector = size === "projector";

  return (
    <div
      className={cn(
        "flex flex-wrap items-center gap-x-5 gap-y-2",
        projector ? "text-lg" : "text-xs",
        theme === "dark" ? "text-neutral-300" : "text-neutral-600",
        className,
      )}
    >
      <ul aria-label={t("legendLabel")} className={cn("flex flex-wrap gap-y-2", projector ? "gap-x-8" : "gap-x-5")}>
        {items.map((item) => (
          <li key={item.key} className="inline-flex items-center gap-2">
            <span
              aria-hidden
              className={cn(
                "inline-flex items-center justify-center gap-1 rounded-full font-bold tabular-nums",
                projector ? "h-9 min-w-[72px] px-3 text-base [&_svg]:h-4 [&_svg]:w-4" : "h-[22px] min-w-[44px] px-2 text-[11px] [&_svg]:h-2.5 [&_svg]:w-2.5",
                item.tone,
              )}
            >
              {item.content}
            </span>
            {item.label}
          </li>
        ))}
      </ul>
      {hint ? <span className={cn("font-semibold", theme === "dark" ? "text-neutral-300" : "text-neutral-600")}>{hint}</span> : null}
      {count ? (
        <span className={cn("ml-auto font-semibold tabular-nums", theme === "dark" ? "text-neutral-400" : "text-neutral-500")}>
          {count}
        </span>
      ) : null}
    </div>
  );
}

/** Whether an element is on screen; `true` where the browser cannot say, so no bar is shown then. */
function useEntryVisible(entryId: string | null, signal: string): boolean {
  const [visible, setVisible] = useState(true);
  useEffect(() => {
    if (!entryId || typeof IntersectionObserver === "undefined") {
      setVisible(true);
      return undefined;
    }
    const target = [`standings-row-${entryId}`, `standings-card-${entryId}`]
      .map((id) => document.getElementById(id))
      .find((element): element is HTMLElement => element !== null && element.getClientRects().length > 0);
    if (!target) {
      // Not on this page, or not drawn: the reader's row is out of sight.
      setVisible(false);
      return undefined;
    }
    const observer = new IntersectionObserver(([entry]) => setVisible(entry ? entry.isIntersecting : true), {
      threshold: 0.6,
    });
    observer.observe(target);
    return () => observer.disconnect();
  }, [entryId, signal]);
  return visible;
}

/** One of the head's numbers: a small capital label over a value and its line. */
function HeadStat({
  label,
  value,
  line,
  theme,
  size,
  valueClass,
  className,
}: {
  label: string;
  value: string;
  line?: string | null;
  theme: StandingsTheme;
  size: BoardSize;
  valueClass?: string;
  className?: string;
}) {
  const projector = size === "projector";
  return (
    <div className={cn("min-w-0 sm:border-l sm:pl-5", projector ? "max-w-[480px]" : "max-w-[260px]", theme === "dark" ? "border-neutral-800" : "border-neutral-200", className)}>
      <p className={cn("font-bold uppercase tracking-[0.09em]", projector ? "text-sm" : "text-[10.5px]", SURFACE[theme].muted)}>{label}</p>
      <p className={cn("mt-[3px] truncate font-bold", projector ? "text-2xl" : "text-[15px]")}>
        <span className={valueClass}>{value}</span>
        {line ? <small className={cn("ml-[7px] font-medium", projector ? "text-base" : "text-xs", SURFACE[theme].muted)}>{line}</small> : null}
      </p>
    </div>
  );
}

/**
 * The scoreboard - `docs/29` §6, computed by the server and never here.
 *
 * The freeze is a cap rather than a snapshot, so the frozen table and the final
 * one are the same request with a different `view`. Which row is the reader's
 * comes from the payload's `you`, because a team contest ranks the team and not
 * the person, and a client deciding that for itself would highlight the wrong
 * row for exactly the contestants who are on a team.
 *
 * `EX-PUB15` added what the field has done to each column, the first solve, and
 * the two things a reader of a three hundred row board needs: a way to find
 * themselves, and a name filter. `EX-SCB03` drew it as design B: one card, the
 * round's numbers in its head, a ring per letter, one-line pills, dark or light.
 * **Every number here is the server's**, and on a frozen board every one of
 * them is capped at the freeze, which is why none of them is recomputed here.
 */
export function ContestStandingsPanel({
  slug,
  locale,
  canModerate,
  kiosk = false,
  onOpenBigScreen,
  problemTitles,
  stickyTop = 65,
  results,
  extraControls,
  virtual = false,
  sponsors,
}: {
  slug: string;
  locale: string;
  canModerate: boolean;
  /**
   * `EX-VRT01`, `docs/29` §6.22: the reader's virtual board, the real field at
   * the try's minute with their row placed in it, from its own read. No
   * Current or Frozen, no filters, no Big screen and no rating columns: a
   * minute of a finished round has none of them.
   */
  virtual?: boolean;
  /**
   * `EX-SCB01`: the projector at the venue. No controls, larger type, a tighter
   * poll, and the pages turn on their own. It is the same request the tab
   * makes, read from the same server cache (`docs/29` §6.1). It always asks for
   * the `public` view: an organizer's own read sees through the freeze, and a
   * venue screen must show what the contestants see. Always dark (Wave 24
   * decision 3).
   */
  kiosk?: boolean;
  /**
   * When the panel lives on the contest page itself, "Big screen" only ever
   * toggles that same route's `?kiosk=1` - a plain `<Link>` there is a soft
   * navigation the page's mount-only effect never sees, so it takes a refresh to
   * take effect. Passing this flips the parent's state directly instead. Left
   * unset (the organizer console's own page) it falls back to the `<Link>`,
   * which is a real cross-route navigation there and remounts correctly.
   */
  onOpenBigScreen?: () => void;
  /**
   * `EX-SCB03`: each letter's problem title in the reader's language, which the
   * standings payload does not carry and the contest page does. It names the
   * letter's link; without it the link is named by the letter alone.
   */
  problemTitles?: Readonly<Record<string, string>>;
  /**
   * `EX-SCB03`: where the phone's letter strip sticks, the bottom edge of the
   * bars that stay on screen above the panel: the Judge bar (64 px and its
   * border) by default, and the contest page's tabs under it there.
   */
  stickyTop?: number;
  /**
   * `EX-SCB05`, Wave 24 decision 6: a result opens its code. The contest's id
   * (the console's reads), whether the reader is signed in, and the contest
   * page's `sourceOpen`; `onOpenSubmissions` is the contest page's Submissions
   * tab. Absent (the Big screen) means no result opens.
   */
  results?: {
    contestId: string;
    signedIn: boolean;
    sourceOpen: boolean;
    onOpenSubmissions?: (target: { letter: string; scope: "mine" | "field" }) => void;
  };
  /**
   * `HR-EXP01`: controls the organizer console adds to the toolbar (its
   * "Download CSV"), given the view the reader has on screen and the card's
   * theme, so a file is asked for in the same view. Never on the Big screen.
   */
  extraControls?: (board: { view: "auto" | "frozen"; theme: StandingsTheme }) => ReactNode;
  /**
   * `EX-SPN01`, `docs/29` §6.23: the contest's sponsors for the head, when the
   * organizer prints them on the standings (the contest page passes them from
   * its own payload). Never on the Big screen, which prints them in its footer.
   */
  sponsors?: readonly SponsorLike[];
}) {
  const t = useTranslations("contest.standings");
  const tDivision = useTranslations("contest.division");
  const tVirtual = useTranslations("contest.virtual");
  const zone = useContestDisplayZone();
  const [view, setView] = useState<"auto" | "frozen">("auto");
  const [page, setPage] = useState(1);
  const [query, setQuery] = useState("");
  // `EX-SCB01`: one institution's rows, offered only when the contest shows the
  // institution (the server ignores the parameter otherwise).
  const [institution, setInstitution] = useState("");
  // `EX-ONS01`: one room's rows, under the same rule for seats.
  const [room, setRoom] = useState("");
  // `EX-DIV03`, Wave 29 decision 4: unofficial rows are off until the reader asks.
  const [showUnofficial, setShowUnofficial] = useState(false);
  // Tiles that changed in the last refresh, for the ring they wear for a few seconds.
  const [fresh, setFresh] = useState<ReadonlySet<string>>(() => new Set());
  const seen = useRef<{ key: string; sigs: Map<string, string> } | null>(null);
  // `EX-SCB03`: the reader's dark or light, the device's setting until they choose.
  const [chosenTheme, setChosenTheme] = useState<StandingsTheme>(initialStandingsTheme);
  const theme: StandingsTheme = kiosk ? "dark" : chosenTheme;
  const size: BoardSize = kiosk ? "projector" : "board";
  // `EX-SCB05`: the result whose code is open, kept as it was pressed so a
  // refresh that moves the row off this page does not close the sheet.
  const [sheet, setSheet] = useState<{ row: StandingsRow; cell: StandingsCell; access: ResultAccess } | null>(null);

  const realStandings = useContestStandings(
    slug,
    {
      view: kiosk ? "public" : view,
      page,
      ...(institution ? { institution } : {}),
      ...(room ? { room } : {}),
      // The Big screen never shows them: it is the rated field's board.
      ...(showUnofficial && !kiosk ? { unofficial: "show" as const } : {}),
    },
    !virtual,
    kiosk ? { refetchInterval: KIOSK_REFETCH_MS } : {},
  );
  const [ghostRunning, setGhostRunning] = useState(true);
  const ghostStandings = useVirtualStandings(slug, page, virtual, ghostRunning);
  const ghost = virtual ? (ghostStandings.data ?? null) : null;
  useEffect(() => {
    if (ghost) setGhostRunning(ghost.virtual.running);
  }, [ghost]);
  const standings = virtual ? ghostStandings : realStandings;
  const data = standings.data;
  const totalPages = data?.totalPages ?? 1;

  // The kiosk turns its own pages: nobody stands at a projector with a mouse.
  useEffect(() => {
    if (!kiosk || totalPages <= 1) return;
    const timer = window.setInterval(() => {
      setPage((value) => (value >= totalPages ? 1 : value + 1));
    }, KIOSK_PAGE_TURN_MS);
    return () => window.clearInterval(timer);
  }, [kiosk, totalPages]);

  // What changed since the last refresh of the SAME page and view. A different
  // page or filter is a different set of rows, not a change, so it resets the
  // memory without marking anything.
  useEffect(() => {
    if (!data) return undefined;
    const key = `${kiosk ? "public" : view}|${data.page}|${institution}|${room}|${showUnofficial}`;
    const sigs = new Map<string, string>();
    for (const item of data.rows) {
      for (const cell of item.cells) {
        sigs.set(`${item.entryId}:${cell.letter}`, `${cell.solved}|${cell.attempts}|${cell.pending}|${cell.score}`);
      }
    }
    const before = seen.current;
    seen.current = { key, sigs };
    if (!before || before.key !== key) return undefined;
    const changed = new Set<string>();
    for (const [cellKey, sig] of sigs) {
      const previous = before.sigs.get(cellKey);
      if (previous !== undefined && previous !== sig) changed.add(cellKey);
    }
    if (changed.size === 0) return undefined;
    setFresh(changed);
    const timer = window.setTimeout(() => setFresh(new Set()), FRESH_MS);
    return () => window.clearTimeout(timer);
  }, [data, kiosk, view, institution, room, showUnofficial]);

  const rows = data?.rows ?? [];
  const needle = query.trim().toLowerCase();
  const visibleRows = useMemo(() => {
    if (!needle) return rows;
    return rows.filter((row) => {
      // `EX-IDN06`: a row that leads with the handle is found by it too.
      const names = [row.identity?.username ?? "", row.name ?? "", ...(row.members ?? []).map((member) => member.name ?? "")];
      return names.some((name) => name.toLowerCase().includes(needle));
    });
  }, [rows, needle]);

  const youEntryId = data?.you?.entryId ?? null;
  const youVisible = useEntryVisible(kiosk ? null : youEntryId, `${page}|${visibleRows.length}|${needle}|${data?.page ?? 0}`);

  if (standings.isPending) {
    return <Skeleton className="h-64 w-full rounded-[22px]" />;
  }

  if (standings.isError || !data) {
    return (
      <p className="rounded-xl border border-[rgb(var(--border))] bg-white p-8 text-center text-sm text-[rgb(var(--muted-foreground))]">
        {t("unavailable")}
      </p>
    );
  }

  const surface = SURFACE[theme];
  const format = data.contest.scoringFormat;
  const offerFrozenToggle = !virtual && !!data.contest.freezeAt && (data.contest.phase === "unfrozen" || canModerate);
  // The columns appear when there is a ledger to draw, which is after the
  // operator applies it (`docs/29` §7.5). A rated contest that has finished but
  // not been rated yet says so instead, because an empty column reads as "you
  // gained nothing".
  const showRating = data.rating.applied;
  // `EX-DIV03`: the board's division, and the switch for the rows outside it,
  // offered only when there are some (the count is the whole field's).
  const division = data.division ?? null;
  const unofficialCount = data.unofficialCount ?? 0;
  const offerUnofficial = !virtual && division !== null && unofficialCount > 0;

  const youRank = data.you?.rank ?? null;
  const youPage = data.you?.page ?? null;
  const youRow = youEntryId !== null ? rows.find((row) => row.entryId === youEntryId) : undefined;
  const youOnThisPage = youRow !== undefined;

  /**
   * Find me: scroll to the row when it is on this page, turn the page when it is
   * not. The rank and the page are the server's answers (`EX-PUB15`), because
   * only the server has folded the whole field; a client with one page of rows
   * cannot know where somebody on page four stands.
   */
  const jumpToYou = () => {
    if (youOnThisPage && youEntryId) {
      const target = [`standings-row-${youEntryId}`, `standings-card-${youEntryId}`]
        .map((id) => document.getElementById(id))
        .find((element): element is HTMLElement => element !== null && element.getClientRects().length > 0);
      target?.scrollIntoView({ block: "center", behavior: smoothScrollBehavior() });
      return;
    }
    if (youPage !== null && youPage !== page) {
      setPage(youPage);
      setQuery("");
    }
  };

  const chooseTheme = (next: StandingsTheme) => {
    setChosenTheme(next);
    rememberStandingsTheme(next);
  };

  const segments = !virtual && data.display?.institution ? (data.segments?.institutions ?? []) : [];
  const rooms = !virtual && data.display?.seat ? (data.segments?.rooms ?? []) : [];
  // `EX-PUB22`: a finished board that is no longer frozen is the final one.
  // `EX-VRT01`: a virtual board is final only once the try is over.
  const final = virtual
    ? ghost !== null && !ghost.virtual.running
    : !data.frozen && (data.contest.phase === "ended" || data.contest.phase === "unfrozen");
  // Not frozen: a pending cell is a submission the judge has not finished.
  const judging = !data.frozen;
  const hasJudging = judging && rows.some((row) => row.cells.some((cell) => cell.pending > 0));
  const filtered = needle !== "" || institution !== "" || room !== "";
  const group = !needle ? zeroGroup(visibleRows, format) : null;
  const columnCount = 3 + data.problems.length + (showRating ? 2 : 0);
  // The Big screen is a venue tool, so a moderator always has it; once the board
  // is final it is the same board every visitor can read, so they may put it on
  // a screen too (`?kiosk=1` was never refused, only the button was hidden).
  const showBigScreen = !kiosk && !virtual && (canModerate || (final && !!onOpenBigScreen));

  // The head's numbers, today's `StandingsSummary` rules (Wave 24, `EX-SCB03`).
  const leader = data.total > 0 ? leaderOf(data, format, filtered) : null;
  const hardest = hardestProblem(data.problems);
  const solves = solveTotal(data.problems);
  const judgingNow = data.frozen ? null : pendingTotal(data.problems);
  const startMs = data.contest.startAt ? Date.parse(data.contest.startAt) : NaN;
  const capMs = data.cap ? Date.parse(data.cap) : NaN;
  const frozenAt = Number.isFinite(startMs) && Number.isFinite(capMs) ? formatContestMinute((capMs - startMs) / 60_000) : null;
  const state: { key: "final" | "frozen" | "live"; label: string } | null = ghost
    ? ghost.virtual.running
      ? { key: "live", label: tVirtual("barChip") }
      : { key: "final", label: tVirtual("resultChip") }
    : final
    ? { key: "final", label: t("stateFinal") }
    : data.frozen
      ? { key: "frozen", label: frozenAt ? t("stateFrozenAt", { at: frozenAt }) : t("view_frozen") }
      : data.contest.phase === "running" || data.contest.phase === "frozen"
        ? { key: "live", label: t("stateLive") }
        : null;
  const projector = size === "projector";
  const columns = `repeat(${data.problems.length <= 6 ? data.problems.length : Math.ceil(data.problems.length / 2)}, minmax(0, 1fr))`;
  const headCell = cn("sticky top-0 z-10", surface.head);
  const problemHref = (letter: string) => (kiosk ? null : `/${locale}/contests/${slug}/problems/${letter}`);
  const problemLabel = (letter: string) => {
    const title = problemTitles?.[letter];
    return title ? t("openProblemTitled", { letter, title }) : t("openProblem", { letter });
  };
  const countLine =
    data.total > 0
      ? t("showing", { from: (data.page - 1) * data.limit + 1, to: Math.min(data.page * data.limit, data.total), total: data.total })
      : null;
  const searchInput =
    theme === "dark"
      ? "border-neutral-800 bg-neutral-900 text-white placeholder:text-neutral-400"
      : "border-neutral-200 bg-neutral-50 text-neutral-900 placeholder:text-neutral-500";
  const selectClass = cn(
    "h-9 rounded-[10px] border px-2.5 text-[13px] font-medium",
    theme === "dark" ? "border-neutral-800 bg-neutral-900 text-white" : "border-neutral-200 bg-white text-neutral-800",
  );

  const reader = !kiosk && results ? { canModerate, signedIn: results.signedIn, sourceOpen: results.sourceOpen } : null;
  const openerFor = (row: StandingsRow): ((cell: StandingsCell) => void) | null => {
    const access = reader ? resultAccessFor(reader, youEntryId === row.entryId) : null;
    return access ? (cell) => setSheet({ row, cell, access }) : null;
  };
  const openHint = reader
    ? reader.canModerate || (reader.sourceOpen && reader.signedIn)
      ? t("openHintAll")
      : youRow && reader.signedIn
        ? t("openHintOwn")
        : null
    : null;
  const sheetRow = sheet ? (rows.find((row) => row.entryId === sheet.row.entryId) ?? sheet.row) : null;
  const sheetCell = sheet && sheetRow ? (sheetRow.cells.find((cell) => cell.letter === sheet.cell.letter) ?? sheet.cell) : null;

  const head = (
    <div className={cn("flex flex-wrap items-center gap-x-5 gap-y-3 border-b", surface.line, projector ? "px-7 py-4" : "px-4 py-3 sm:pl-[22px]")}>
      <div className="min-w-[180px]">
        <h2 className={cn("flex flex-wrap items-center gap-2 font-bold tracking-tight", projector ? "text-2xl" : "text-lg")}>
          {ghost
            ? ghost.virtual.running
              ? tVirtual("boardTitle", { minute: formatNumber(ghost.virtual.minute, locale) })
              : tVirtual("boardEndedTitle")
            : t("title")}
          {/* `EX-DIV03`: whose board this is; the U rows below are measured against it. */}
          {division ? <DivisionTag division={division} locale={locale} tone={theme} /> : null}
        </h2>
        <p className={cn("mt-[3px] flex flex-wrap items-center gap-x-[7px] whitespace-nowrap", projector ? "text-base" : "text-xs", surface.muted)}>
          {state ? (
            <span
              data-board-state={state.key}
              className={cn(
                "inline-flex items-center gap-1.5 font-bold",
                state.key === "final" && (theme === "dark" ? "text-scoreboard-gold" : "text-scoreboard-gold-ink"),
                state.key === "frozen" && (theme === "dark" ? "text-scoreboard-frozen-night-ink" : "text-scoreboard-frozen-day-ink"),
                state.key === "live" && (theme === "dark" ? "text-scoreboard-ring-night" : "text-scoreboard-count-day"),
              )}
            >
              {state.key === "frozen" ? <Snowflake className="h-3 w-3" aria-hidden /> : null}
              {state.key === "live" ? <i aria-hidden className="block h-[7px] w-[7px] rounded-full bg-current" /> : null}
              {state.label}
            </span>
          ) : null}
          <span>
            {state ? "· " : ""}
            {t(data.participation === "team" ? "fieldLineTeams" : "fieldLine", { count: data.total, problems: data.problems.length })}
          </span>
        </p>
        {ghost?.virtual.running ? (
          <p className={cn("mt-1.5 max-w-xl text-[13px] leading-relaxed", surface.muted)}>{tVirtual("boardBody")}</p>
        ) : null}
      </div>
      {ghost && youRank !== null ? (
        <HeadStat
          theme={theme}
          size={size}
          label={tVirtual("youStand")}
          value={tVirtual("rankOf", { rank: formatNumber(youRank, locale), total: formatNumber(data.total, locale) })}
          valueClass="text-primary"
        />
      ) : null}
      {leader ? (
        <HeadStat
          theme={theme}
          size={size}
          label={t(final ? "summaryWinner" : data.frozen ? "summaryLeadingFrozen" : "summaryLeading")}
          value={rowLabel(leader)}
          valueClass={nameTone(leader.currentRating, theme)}
          line={
            format === "icpc"
              ? t("summaryLeaderScoreIcpc", { solved: leader.solved, penalty: leader.penaltyMinutes })
              : t("summaryLeaderScorePoints", { score: leader.score })
          }
        />
      ) : null}
      {judgingNow !== null && judgingNow > 0 ? (
        <HeadStat
          theme={theme}
          size={size}
          className="hidden sm:block"
          label={t("summaryJudging")}
          value={t("summaryJudgingValue", { count: judgingNow })}
        />
      ) : solves !== null ? (
        <HeadStat
          theme={theme}
          size={size}
          className="hidden sm:block"
          label={t("summarySolves")}
          value={String(solves)}
          line={t("summarySolvesSub", { count: data.problems.length })}
        />
      ) : null}
      {hardest ? (
        <HeadStat
          theme={theme}
          size={size}
          className="hidden sm:block"
          label={t("summaryHardest")}
          value={hardest.letter}
          line={
            hardest.solved === 0
              ? t("summaryHardestNone", { tried: hardest.tried })
              : t("summaryHardestSub", { solved: hardest.solved, tried: hardest.tried })
          }
        />
      ) : null}
      {kiosk ? null : (
        <div className="flex w-full flex-wrap items-center gap-2 sm:ml-auto sm:w-auto">
          {offerFrozenToggle ? (
            <SegmentedControl
              label={t("viewLabel")}
              size="sm"
              tone={theme}
              value={view}
              onChange={(value) => {
                setView(value);
                setPage(1);
              }}
              options={[
                { value: "auto", label: t("view_auto") },
                { value: "frozen", label: t("view_frozen"), icon: <Snowflake aria-hidden /> },
              ]}
            />
          ) : null}
          {segments.length > 0 ? (
            <select
              value={institution}
              onChange={(event) => {
                setInstitution(event.target.value);
                setPage(1);
              }}
              aria-label={t("institutionFilterLabel")}
              className={selectClass}
            >
              <option value="">{t("institutionFilterAll")}</option>
              {segments.map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </select>
          ) : null}
          {rooms.length > 0 ? (
            <select
              value={room}
              onChange={(event) => {
                setRoom(event.target.value);
                setPage(1);
              }}
              aria-label={t("roomFilterLabel")}
              className={selectClass}
            >
              <option value="">{t("roomFilterAll")}</option>
              {rooms.map((entry) => (
                <option key={entry.id} value={entry.id}>
                  {entry.name}
                </option>
              ))}
            </select>
          ) : null}
          {offerUnofficial ? (
            <Button
              type="button"
              variant={theme === "dark" ? "outlineDark" : "filter"}
              size="sm"
              aria-pressed={showUnofficial}
              data-testid="show-unofficial"
              onClick={() => {
                setShowUnofficial((value) => !value);
                setPage(1);
              }}
            >
              {t("showUnofficial", { count: formatNumber(unofficialCount, locale) })}
            </Button>
          ) : null}
          {extraControls ? extraControls({ view, theme }) : null}
          {showBigScreen ? (
            onOpenBigScreen ? (
              <Button
                type="button"
                variant={theme === "dark" ? "ghostDark" : "outline"}
                size="smIcon"
                onClick={onOpenBigScreen}
                aria-label={t("bigScreen")}
                title={t("bigScreen")}
              >
                <MonitorPlay className="h-4 w-4" aria-hidden />
              </Button>
            ) : (
              <Link
                href={`/${locale}/contests/${slug}?kiosk=1`}
                aria-label={t("bigScreen")}
                title={t("bigScreen")}
                className={buttonVariants({ variant: theme === "dark" ? "ghostDark" : "outline", size: "smIcon" })}
              >
                <MonitorPlay className="h-4 w-4" aria-hidden />
              </Link>
            )
          ) : null}
          <div className="relative min-w-[10rem] flex-1 sm:w-[232px] sm:flex-none">
            <Search className={cn("pointer-events-none absolute left-3 top-1/2 h-[15px] w-[15px] -translate-y-1/2", surface.muted)} aria-hidden />
            <Input
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder={t("searchPlaceholder")}
              aria-label={t("searchPlaceholder")}
              className={cn("h-9 min-h-9 rounded-[10px] py-0 pl-9 text-[13.5px]", searchInput)}
            />
          </div>
          <SegmentedControl
            label={t("themeLabel")}
            size="sm"
            tone={theme}
            value={theme}
            onChange={chooseTheme}
            options={[
              { value: "dark", label: t("themeDark"), icon: <Moon aria-hidden />, iconOnly: true },
              { value: "light", label: t("themeLight"), icon: <Sun aria-hidden />, iconOnly: true },
            ]}
          />
        </div>
      )}
      {/* `EX-SPN01`: the sponsors along the head's bottom edge, when the organizer prints them here. */}
      {sponsors?.length ? (
        <SponsorStrip
          sponsors={sponsors}
          tone={theme}
          size="compact"
          className={cn("w-full border-t pt-3", surface.line)}
        />
      ) : null}
    </div>
  );

  const emptyLine = (text: string) => (
    <p className={cn("p-8 text-center text-sm", surface.muted)}>{text}</p>
  );

  return (
    <div className={cn(kiosk ? "flex h-full min-h-0 flex-col gap-3" : "space-y-3")}>
      {data.frozen && !kiosk ? (
        <div className="flex items-start gap-3.5 rounded-xl border-[1.5px] border-blue-200 bg-blue-50 px-4 py-3 text-sm leading-relaxed text-blue-900">
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[10px] bg-blue-900 text-white">
            <Snowflake className="h-[17px] w-[17px]" aria-hidden />
          </span>
          <p className="pt-1">
            {t("frozenNotice", { at: formatDateTime(data.cap, locale, zone) })}
            {/* `EX-PUB22`: after the end, the one person who can publish it. */}
            {data.contest.phase === "ended" ? (
              <>
                {" "}
                <span>{t("frozenAfterEnd")}</span>
              </>
            ) : null}
          </p>
        </div>
      ) : null}

      {data.rating.rated && !data.rating.applied && !kiosk ? (
        <div className="flex items-start gap-2 rounded-xl border border-violet-200 bg-violet-50 px-4 py-3 text-sm text-violet-900">
          <TrendingUp className="mt-0.5 h-4 w-4 shrink-0" />
          <p>{t("ratingPending")}</p>
        </div>
      ) : null}

      {/* `isolate`: the board is its own stacking context, so its sticky corner
          cells (z-20) stay under the page's sticky contest tabs (also z-20,
          but earlier in the page) when the table scrolls up beneath them.
          `overflow-clip`: rounds the corners without making the board a scroll
          container, which `overflow-hidden` did, so the phone's letter strip
          sticks under the tabs instead of scrolling away with the board. */}
      <section
        aria-label={t("title")}
        data-board-theme={theme}
        className={cn(
          "isolate overflow-hidden supports-[overflow:clip]:overflow-clip rounded-[22px] antialiased transition-colors duration-200",
          surface.card,
          kiosk && "flex min-h-0 flex-1 flex-col",
        )}
      >
        {head}

        {rows.length === 0 ? (
          emptyLine(t("empty"))
        ) : visibleRows.length === 0 ? (
          emptyLine(t("noMatches"))
        ) : (
          <>
            {/* The table, from `sm` up: its own scroll box, so the header and the
                rank and name stay put while the problems scroll. */}
            <div
              className={cn(
                "hidden overflow-auto [scrollbar-width:thin] sm:block",
                kiosk ? "min-h-0 flex-1" : "max-h-[72vh]",
              )}
            >
              <table className="w-full min-w-[560px] border-separate border-spacing-0">
                <thead>
                  <tr
                    className={cn(
                      "font-bold uppercase tracking-[0.09em]",
                      projector ? "text-sm" : "text-[10.5px]",
                      surface.muted,
                    )}
                  >
                    <th className={cn(headCell, STICKY[size].rank, "z-20 px-2 pb-[11px] text-center align-bottom")}>
                      <span aria-hidden>#</span>
                      <span className="sr-only">{t("rank")}</span>
                    </th>
                    <th className={cn(headCell, STICKY[size].name, "z-20 pb-[11px] pl-1.5 pr-3 text-left align-bottom")}>
                      {data.participation === "team" ? t("team") : t("contestant")}
                    </th>
                    <th
                      className={cn(headCell, "whitespace-nowrap px-3 pb-[11px] text-center align-bottom")}
                      title={format === "icpc" ? t("solvedPenTitle") : undefined}
                    >
                      {format === "icpc" ? t("solvedPen") : t("score")}
                    </th>
                    {data.problems.map((problem) => (
                      <th
                        key={problem.letter}
                        className={cn(
                          headCell,
                          "px-1 pb-[9px] pt-2 align-bottom normal-case tracking-normal",
                          projector ? "min-w-[120px]" : "min-w-[84px]",
                        )}
                      >
                        <span className="flex items-end justify-center gap-[7px]">
                          <span className="flex flex-col items-center">
                            {problem.balloonColor ? <Balloon color={problem.balloonColor} theme={theme} size={size} /> : null}
                            <ProblemRing
                              problem={problem}
                              theme={theme}
                              size={size}
                              href={problemHref(problem.letter)}
                              label={problemLabel(problem.letter)}
                            />
                          </span>
                          <span className="flex flex-col items-start pb-0.5">
                            {problem.maxScore !== null ? (
                              <span className={cn("text-[10.5px] font-semibold tabular-nums", surface.muted)}>{problem.maxScore}</span>
                            ) : null}
                            <ColumnCounts problem={problem} theme={theme} size={size} withTitle />
                          </span>
                        </span>
                      </th>
                    ))}
                    {showRating ? (
                      <>
                        <th className={cn(headCell, "px-3 pb-[11px] text-center align-bottom")}>{t("rating")}</th>
                        <th className={cn(headCell, "px-3 pb-[11px] text-center align-bottom")}>{t("ratingDelta")}</th>
                      </>
                    ) : null}
                  </tr>
                </thead>
                <tbody>
                  {visibleRows.map((row, index) => (
                    <Fragment key={row.entryId}>
                      {group && index === group.firstIndex ? (
                        <tr>
                          <td
                            colSpan={columnCount}
                            className={cn(
                              "border-b px-6 py-2 font-semibold",
                              projector ? "text-base" : "text-[13px]",
                              theme === "dark" ? "border-neutral-800 bg-neutral-900 text-neutral-300" : "border-neutral-200 bg-neutral-50 text-neutral-600",
                            )}
                          >
                            {group.shareRank
                              ? data.totalPages === 1
                                ? t("tieShared", { count: group.count, rank: group.rank })
                                : t("tieSharedMany", { rank: group.rank })
                              : t("tiePlain")}
                          </td>
                        </tr>
                      ) : null}
                      <BoardRow
                        row={row}
                        format={format}
                        isYou={youEntryId === row.entryId}
                        showRating={showRating}
                        final={final}
                        judging={judging}
                        theme={theme}
                        size={size}
                        fresh={fresh}
                        onOpen={openerFor(row)}
                      />
                    </Fragment>
                  ))}
                </tbody>
              </table>
            </div>

            {/* The phone: a sticky strip of letters, then one entry per row. */}
            <div className="sm:hidden">
              <div
                className={cn("sticky z-10 grid gap-1.5 border-b px-4 py-2", surface.line, surface.cell)}
                style={{ gridTemplateColumns: columns, top: stickyTop }}
              >
                {data.problems.map((problem) => (
                  <span key={problem.letter} className="flex flex-col items-center">
                    {problem.balloonColor ? (
                      <Balloon color={problem.balloonColor} theme={theme} size="board" phone label={false} />
                    ) : null}
                    <ProblemRing
                      problem={problem}
                      theme={theme}
                      size="board"
                      phone
                      href={problemHref(problem.letter)}
                      label={problemLabel(problem.letter)}
                    />
                    {problem.solvedCount !== undefined ? (
                      <span className={cn("mt-[3px] text-[10.5px] font-semibold tabular-nums", surface.muted)}>
                        <b className={theme === "dark" ? "text-scoreboard-ring-night" : "text-scoreboard-count-day"}>{problem.solvedCount}</b>
                        {`/${problem.attemptedCount ?? problem.solvedCount}`}
                      </span>
                    ) : null}
                  </span>
                ))}
              </div>
              {visibleRows.map((row) => (
                <PhoneRow
                  key={row.entryId}
                  row={row}
                  format={format}
                  isYou={youEntryId === row.entryId}
                  final={final}
                  judging={judging}
                  theme={theme}
                  columns={columns}
                  fresh={fresh}
                  onOpen={openerFor(row)}
                />
              ))}
            </div>
          </>
        )}

        {kiosk ? null : (
          <Legend
            format={format}
            frozen={data.frozen}
            hasJudging={hasJudging}
            theme={theme}
            size={size}
            count={countLine}
            hint={openHint}
            className={cn("border-t px-4 py-[11px] sm:px-[22px]", surface.legend)}
          />
        )}
      </section>

      {kiosk ? (
        <div className="flex flex-wrap items-center gap-x-8 gap-y-2 border-t border-neutral-800 pt-3 text-lg text-neutral-300">
          <Legend format={format} frozen={data.frozen} hasJudging={hasJudging} theme="dark" size="projector" className="flex-1" />
          {data.totalPages > 1 ? (
            <span className="inline-flex items-center gap-3">
              {t("pageOf", { page: data.page, pages: data.totalPages })}
              {data.totalPages <= 12 ? (
                <span aria-hidden className="inline-flex items-center gap-2">
                  {Array.from({ length: data.totalPages }, (_, index) => (
                    <i
                      key={index}
                      className={cn("block h-1.5 w-[34px] rounded-full", index + 1 === data.page ? "bg-white" : "bg-neutral-700")}
                    />
                  ))}
                </span>
              ) : null}
            </span>
          ) : null}
          <span className="text-base">
            {t("updatedAt", {
              at: new Date(standings.dataUpdatedAt || Date.now()).toLocaleTimeString(
                locale === "bn" ? "bn-BD" : "en-GB",
                { timeStyle: "medium" },
              ),
            })}
          </span>
        </div>
      ) : (
        <>
          <p className="text-[12.5px] font-medium text-neutral-600">
            {format === "icpc" ? t("rankRule", { minutes: ICPC_PENALTY_MINUTES_PER_WRONG }) : t("rankRulePoints")}{" "}
            {t("ringRule")}
          </p>
          {division ? (
            <p className="text-[12.5px] font-medium text-neutral-600" data-testid="division-board-note">
              {/* `EX-DIV03`, decisions 3 and 4: what U means, and who the ranks count. */}
              {showUnofficial && unofficialCount > 0
                ? t("unofficialRule", { label: division.label ?? tDivision("customTag") })
                : null}{" "}
              {t("ratedRanksRule")}
            </p>
          ) : null}
          {data.totalPages > 1 ? (
            <div className="flex items-center justify-center gap-3">
              <Button variant="tertiary"
                type="button"
                onClick={() => setPage((value) => Math.max(1, value - 1))}
                disabled={page <= 1}
                className="inline-flex h-11 items-center gap-1 rounded-lg border border-[rgb(var(--border))] bg-white px-2.5 text-xs font-medium text-neutral-700 transition-colors hover:bg-neutral-50 disabled:cursor-not-allowed disabled:opacity-40"
              >
                <ChevronLeft className="h-3.5 w-3.5" />
                {t("previous")}
              </Button>
              <span className="text-xs text-[rgb(var(--muted-foreground))]">
                {t("pageOf", { page: data.page, pages: data.totalPages })}
              </span>
              <Button variant="tertiary"
                type="button"
                onClick={() => setPage((value) => Math.min(data.totalPages, value + 1))}
                disabled={page >= data.totalPages}
                className="inline-flex h-11 items-center gap-1 rounded-lg border border-[rgb(var(--border))] bg-white px-2.5 text-xs font-medium text-neutral-700 transition-colors hover:bg-neutral-50 disabled:cursor-not-allowed disabled:opacity-40"
              >
                {t("next")}
                <ChevronRight className="h-3.5 w-3.5" />
              </Button>
            </div>
          ) : null}
        </>
      )}

      {!kiosk && youRank !== null && !youVisible ? (
        <div
          role="status"
          className={cn(
            "sticky bottom-3 z-30 mx-auto flex w-max max-w-full flex-wrap items-center gap-x-3.5 gap-y-2 rounded-full py-[7px] pl-2.5 pr-[7px] shadow-[0_18px_40px_-12px_rgba(0,0,0,0.5)]",
            theme === "dark" ? "bg-white text-neutral-900" : "bg-neutral-900 text-white",
          )}
        >
          <span className="rounded-full bg-primary-ink px-2 py-0.5 text-[10.5px] font-bold tracking-wide text-white">{t("you")}</span>
          <b className="text-sm">{t("youBarRank", { rank: youRank, total: data.total })}</b>
          {youRow ? (
            <span className={cn("hidden text-[13px] sm:inline", theme === "dark" ? "text-neutral-500" : "text-neutral-300")}>
              {format === "icpc"
                ? t("youBarLine", { solved: youRow.solved, penalty: youRow.penaltyMinutes })
                : t("youBarLinePoints", { score: youRow.score })}
            </span>
          ) : null}
          <Button
            variant="tertiary"
            type="button"
            onClick={jumpToYou}
            className="inline-flex h-[34px] items-center gap-[7px] rounded-full bg-primary-ink px-3.5 text-[13px] font-bold text-white hover:bg-primary-ink/90 hover:text-white"
          >
            <Crosshair className="h-[15px] w-[15px]" aria-hidden />
            {t(youOnThisPage ? "jumpToMe" : "goToMyPage")}
          </Button>
        </div>
      ) : null}

      {sheet && sheetRow && sheetCell && results ? (
        <ResultSheet
          slug={slug}
          contestId={results.contestId}
          locale={locale}
          theme={theme}
          access={sheet.access}
          row={sheetRow}
          cell={sheetCell}
          problem={data.problems.find((problem) => problem.letter === sheetCell.letter)}
          problemTitle={problemTitles?.[sheetCell.letter]}
          contest={data.contest}
          onClose={() => setSheet(null)}
          onOpenSubmissions={results.onOpenSubmissions}
        />
      ) : null}
    </div>
  );
}
